import React, { useState, useMemo } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Form, Input, Select } from '@rocketseat/unform';
import { FaFileContract } from 'react-icons/fa';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import { Container, ButtonContainer } from './styles';
import api from '~/services/api';
import history from '~/services/history';
import FeesPreviewTable from '~/components/FeesPreviewTable';

export default function Simulation({ location }) {
  const item =
    location.state && location.state.item ? location.state.item : undefined;

  async function handleSubmit(data) {
    try {
      const response = await api.post(
        'simulation',
        {
          ...data,
          vehicle_id: item.id,
        },
        {
          responseType: 'blob',
        }
      );
      fileDownload(response.data, `SIMULATION-${item.vin}.pdf`);
      toast.success('Downloading...');
      history.push('/inventory');
    } catch (err) {
      toast.error(
        err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Error while generating simulation'
      );
    }
  }
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const [term, setTerm] = useState('');
  const [uber, setUber] = useState('false');
  const [activationValue, setActivationValue] = useState('');
  const [monthlyValue, setMonthlyValue] = useState('');

  const monthly = useMemo(() => {
    switch (term) {
      case '6':
        return item.monthly.six;
      case '12':
        return item.monthly.twelve;
      case '24':
        return item.monthly.twentyFour;
      default:
        return '???';
    }
  }, [item.monthly.six, item.monthly.twelve, item.monthly.twentyFour, term]);
  const activation = useMemo(() => {
    switch (term) {
      case '6':
        return item.activation.six;
      case '12':
        return item.activation.twelve;
      case '24':
        return item.activation.twentyFour;
      default:
        return '???';
    }
  }, [
    item.activation.six,
    item.activation.twelve,
    item.activation.twentyFour,
    term,
  ]);

  const schema = Yup.object().shape({
    term: Yup.number('Should be a number')
      .transform((value, originalValue) => {
        return originalValue.trim() === '' ? undefined : value;
      })
      .required(),
    uber: Yup.string('Should be a string').transform((value, originalValue) => {
      return originalValue.trim() === '' ? 'false' : value;
    }),
    monthly: Yup.number('Should be a number')
      .nullable(true)
      .transform((value, originalValue) =>
        originalValue.trim() === '' ? null : value
      ),
    activation: Yup.number('Should be a number')
      .nullable(true)
      .transform((value, originalValue) =>
        originalValue.trim() === '' ? null : value
      ),
  });
  return (
    <Container>
      <h1>Simulation</h1>
      <h2>{item.displayName}</h2>
      <Form schema={schema} onSubmit={handleSubmit}>
        <Select
          onChange={event => {
            setMonthlyValue('');
            setActivationValue('');
            if (event.target.value === 'true') {
              if (term) setTerm('6');
            }
            setUber(event.target.value);
          }}
          placeholder={
            !item.uber ? 'NOT ALLOWED TO DO UBER' : 'Select an option'
          }
          name="uber"
          label="Will the client use for uber?"
          disabled={!item.uber}
          value={uber}
          options={[
            {
              id: true,
              title: 'Yes',
            },
            {
              id: false,
              title: 'No',
            },
          ]}
        />
        <Select
          onChange={event => {
            if (event.target.value !== term) {
              setMonthlyValue('');
              setActivationValue('');
            }
            setTerm(event.target.value);
          }}
          name="term"
          label="Select a Term:"
          options={
            uber === 'true'
              ? [
                  {
                    id: 6,
                    title: '6 Months',
                  },
                ]
              : [
                  {
                    id: 6,
                    title: '6 Months',
                  },
                  {
                    id: 12,
                    title: '12 Months',
                  },
                  {
                    id: 24,
                    title: '24 Months',
                  },
                ]
          }
        />
        <Input
          label="Monthly"
          name="monthly"
          disabled={!term}
          placeholder={formatter.format(monthly)}
          value={monthlyValue}
          onChange={event => setMonthlyValue(event.target.value)}
        />
        <Input
          label="Activation"
          name="activation"
          disabled={!term}
          placeholder={formatter.format(activation)}
          value={activationValue}
          onChange={event => setActivationValue(event.target.value)}
        />
        {term && (
          <FeesPreviewTable
            term={term}
            uber={uber === 'true'}
            vehicle={item}
            monthly={monthlyValue || monthly}
            activation={activationValue || activation}
          />
        )}
        <ButtonContainer>
          <button type="submit">
            <FaFileContract color="#FFF" size={22} />
            Generate Simulation
          </button>
        </ButtonContainer>
      </Form>
    </Container>
  );
}

Simulation.propTypes = {
  location: PropTypes.shape({
    state: PropTypes.shape({
      item: PropTypes.shape(),
    }),
  }).isRequired,
};
